import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type { DexEntry, DexSummary, EggAppearance, Pokemon, Species } from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Panel, Tabs, TypeChip } from '../components/ui';
import { PokemonCard } from '../components/game';
import { EggArt } from '../components/EggArt';
import { ALL_TYPES, dexNumber, timeAgo, titleCase } from '../lib/format';

/**
 * The Pokedex, in tabs.
 *
 * The Shiny Dex is not a filter on the normal one - it is the same grid read
 * with a different question, so `mode` changes what every control *means*:
 * "Recorded" means "you have owned a shiny of this", the sprite shown is the
 * shiny sprite, and completion is shiny completion. One flag rather than a
 * parallel screen, so the two can never drift apart.
 */
type DexMode = 'normal' | 'shiny';
type DexTab = 'dex' | 'shiny' | 'regional' | 'stats';

export function DexPage() {
  const [tab, setTab] = useState<DexTab>('dex');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [type, setType] = useState('');
  const [generation, setGeneration] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const mode: DexMode = tab === 'shiny' ? 'shiny' : 'normal';

  const state = useAsync<{ entries: DexEntry[]; summary: DexSummary }>(
    () =>
      api.get('/dex', {
        search: debounced || undefined,
        type: type || undefined,
        // The Regional tab is the same grid pinned to one region; defaulting it
        // to Kanto rather than "all" is what makes it a different tab at all.
        generation: tab === 'regional' ? generation || '1' : generation || undefined,
        mode,
        status: status || undefined,
      }),
    [debounced, type, generation, status, mode, tab],
  );

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const summary = state.data?.summary;
  const shiny = mode === 'shiny';

  return (
    <div className={`stack ${shiny ? 'dex--shiny' : ''}`}>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{shiny ? 'Shiny Dex' : 'Pokédex'}</h1>
          <div className="page-head__sub">
            {summary
              ? shiny
                ? `${summary.shinySpecies} of ${summary.totalSpecies} recorded in shiny · ${summary.totalShinyObtained} obtained`
                : `${summary.ownedSpecies} of ${summary.totalSpecies} species · ${summary.totalObtained} obtained`
              : 'Loading...'}
          </div>
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={(next) => {
          setTab(next);
          setStatus('');
        }}
        tabs={[
          { value: 'dex', label: 'Pokédex' },
          { value: 'shiny', label: '✦ Shiny Dex', badge: summary?.shinySpecies || undefined },
          { value: 'regional', label: 'Regional' },
          { value: 'stats', label: 'Statistics' },
        ]}
      />

      {tab === 'stats' ? (
        <DexStatistics summary={summary} loading={state.loading} />
      ) : (
        <>
          {summary && (
            <Panel title={shiny ? '✦ Shiny completion' : '▤ Completion'}>
              <div className="stack">
                <div className="stack-sm">
                  <div className="row row--between tiny mono">
                    <span>Overall</span>
                    <span>
                      {Math.round((shiny ? summary.shinyCompletion : summary.completion) * 100)}%
                    </span>
                  </div>
                  <Bar
                    ratio={shiny ? summary.shinyCompletion : summary.completion}
                    tall
                    variant={shiny ? 'egg' : 'ready'}
                  />
                </div>

                <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  {summary.byGeneration.map((row) => {
                    const have = shiny ? row.shiny : row.owned;
                    return (
                      <div key={row.generation} className="stack-sm">
                        <div className="row row--between tiny mono">
                          <span>{row.name}</span>
                          <span className="muted">
                            {have}/{row.total}
                          </span>
                        </div>
                        <Bar
                          ratio={row.total === 0 ? 0 : have / row.total}
                          variant={shiny ? 'egg' : 'default'}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </Panel>
          )}

          <Panel title="Filters" raised={false}>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--gap-sm)' }}>
              <div className="field">
                <label className="field__label" htmlFor="dex-search">Search</label>
                <input
                  id="dex-search"
                  className="input"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Name or number"
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="dex-type">Type</label>
                <select id="dex-type" className="select" value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="">All types</option>
                  {ALL_TYPES.map((option) => (
                    <option key={option} value={option}>{titleCase(option)}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="field__label" htmlFor="dex-gen">Region</label>
                <select id="dex-gen" className="select" value={generation} onChange={(e) => setGeneration(e.target.value)}>
                  {tab !== 'regional' && <option value="">All regions</option>}
                  <option value="1">Kanto</option>
                  <option value="2">Johto</option>
                </select>
              </div>
              <div className="field">
                <label className="field__label" htmlFor="dex-status">Status</label>
                <select id="dex-status" className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="owned">{shiny ? 'Shiny recorded' : 'Recorded'}</option>
                  <option value="missing">{shiny ? 'No shiny yet' : 'Not yet recorded'}</option>
                  <option value="holding">Currently holding</option>
                </select>
              </div>
            </div>
          </Panel>

          <Panel title="Entries" hint={state.data ? `${state.data.entries.length} shown` : ''}>
            {state.loading ? (
              <div className="dex-grid">
                {Array.from({ length: 24 }, (_, index) => (
                  <div key={index} className="pxwell skeleton" style={{ height: 110 }} />
                ))}
              </div>
            ) : state.data && state.data.entries.length > 0 ? (
              <div className="dex-grid">
                {state.data.entries.map((entry) => (
                  <DexCell key={entry.id} entry={entry} shiny={shiny} />
                ))}
              </div>
            ) : (
              <EmptyState art="◌" title="No entries match">
                Try a different filter combination.
              </EmptyState>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

/**
 * One cell.
 *
 * In shiny mode "recorded" means the shiny is recorded, so an entry you own
 * normally but have never seen shiny is *silhouetted* - that is exactly the
 * information a shiny hunter opens this page for.
 */
function DexCell({ entry, shiny }: { entry: DexEntry; shiny: boolean }) {
  const recorded = shiny ? entry.shinyOwned : entry.owned;
  const held = shiny ? entry.holdingShiny : entry.holding;
  const count = shiny ? entry.shinyOwnedCount : entry.ownedCount;

  return (
    <Link
      to={`/dex/${entry.id}`}
      className={`pxframe dex-cell ${recorded ? '' : 'dex-cell--missing'} ${
        shiny && recorded ? 'dex-cell--shiny' : ''
      }`}
    >
      <div style={{ position: 'relative' }}>
        {entry.shinyOwned && !shiny && (
          <span className="shiny-star" aria-hidden style={{ right: -4, top: -4 }}>✦</span>
        )}
        <img
          className="pixel"
          src={shiny || entry.shinyOwned ? entry.spriteShiny : entry.sprite}
          alt=""
          loading="lazy"
        />
      </div>
      <span className="dex-cell__no">{dexNumber(entry.id)}</span>
      {/* The name of an unrecorded species is still hidden in the normal dex,
          but never in shiny mode - you already know what a Gyarados is, and
          hiding it would just make the shiny grid unreadable. */}
      <span className="dex-cell__name">{entry.owned || shiny ? entry.name : '???'}</span>
      {count > 1 && <span className="dex-cell__owned">x{count}</span>}
      {held === 0 && recorded && (
        <span className="dex-cell__owned" title="Recorded, but you no longer hold one">
          ✓
        </span>
      )}
    </Link>
  );
}

/** The Statistics tab: completion cut by region and by type, plus recent shinies. */
function DexStatistics({ summary, loading }: { summary?: DexSummary; loading: boolean }) {
  if (loading || !summary) return <div className="pxwell skeleton" style={{ height: 320 }} />;

  return (
    <div className="stack">
      <Panel title="◈ At a glance">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Stat label="Species recorded" value={`${summary.ownedSpecies} / ${summary.totalSpecies}`} />
          <Stat label="Shiny recorded" value={`${summary.shinySpecies} / ${summary.totalSpecies}`} />
          <Stat label="Pokémon obtained" value={String(summary.totalObtained)} />
          <Stat label="Shinies obtained" value={String(summary.totalShinyObtained)} />
        </div>
      </Panel>

      <Panel title="▤ By region">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          {summary.byGeneration.map((row) => (
            <div key={row.generation} className="stack-sm">
              <div className="row row--between tiny mono">
                <span>{row.name}</span>
                <span className="muted">{row.owned}/{row.total}</span>
              </div>
              <Bar ratio={row.total === 0 ? 0 : row.owned / row.total} variant="ready" />
              <div className="row row--between tiny mono">
                <span className="muted">✦ shiny</span>
                <span className="muted">{row.shiny}/{row.total}</span>
              </div>
              <Bar ratio={row.total === 0 ? 0 : row.shiny / row.total} variant="egg" />
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="◉ By type" hint="Feeds the Type Mastery badges">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          {summary.byType.map((row) => (
            <div key={row.type} className="stack-sm">
              <div className="row row--between tiny">
                <TypeChip type={row.type} />
                <span className="mono muted">{row.owned}/{row.total}</span>
              </div>
              <Bar ratio={row.total === 0 ? 0 : row.owned / row.total} />
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="✦ Recent shiny discoveries">
        {summary.recentShinies.length === 0 ? (
          <EmptyState art="✦" title="No shinies yet">
            The first one is the one you remember.
          </EmptyState>
        ) : (
          <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
            {summary.recentShinies.map((entry) => (
              <Link key={entry.id} to={`/dex/${entry.id}`} className="center" style={{ width: 84 }}>
                {entry.sprite && <img className="pixel" src={entry.sprite} alt="" style={{ width: 64, height: 64 }} />}
                <div className="tiny mono">{entry.name}</div>
                <div className="tiny muted">{timeAgo(entry.at)}</div>
              </Link>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
      <div className="mono" style={{ fontSize: 'var(--text-md)' }}>{value}</div>
      <div className="tiny muted">{label}</div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

interface DexDetail {
  species: Species;
  evolvesTo: Array<{ id: number; name: string; sprite: string; requirementText: string }>;
  evolvesFrom: Array<{ id: number; name: string; sprite: string; requirementText: string }>;
  owned: Pokemon[];
  /** Only families you have personally hatched this species from. */
  foundIn: Array<{
    key: string;
    name: string;
    theme: string;
    appearance: EggAppearance;
    discoveredAt: string;
  }>;
}

/**
 * Starts (or resumes) a Shiny hunt for this evolutionary family.
 *
 * Resuming matters: the server keys a hunt on the family root and keeps one row
 * per player per family, so coming back to Eevee months later picks up the
 * lifetime totals and the bad-luck protection rather than starting over.
 */
function HuntButton({ speciesId, name }: { speciesId: number; name: string }) {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);

  async function start() {
    setBusy(true);
    try {
      await api.post('/breeder/hunt/start', { speciesId });
      push('success', `Hunting ${name}. Eggs from its family now build a chain.`, '✦');
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not start the hunt.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button size="sm" loading={busy} onClick={() => void start()}>
      ✦ Hunt this family
    </Button>
  );
}

export function DexEntryPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const state = useAsync<DexDetail>(() => api.get(`/dex/${id}`), [id]);
  const [showShiny, setShowShiny] = useState(false);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 400 }} />;

  const { species, evolvesTo, evolvesFrom, owned, foundIn } = state.data;

  return (
    <div className="stack">
      <div className="row">
        <Button size="sm" variant="ghost" onClick={() => navigate(-1)} style={{ color: 'var(--paper)' }}>
          ← Back
        </Button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(260px, 1fr) minmax(280px, 1.3fr)', alignItems: 'start' }}>
        <Panel>
          <div className="stack center">
            <img
              className="pixel"
              src={showShiny ? species.spriteShiny : species.sprite}
              alt={species.displayName}
              style={{ width: 168, height: 168, objectFit: 'contain', margin: '0 auto', animation: 'bob 4s ease-in-out infinite' }}
            />
            <Button size="sm" variant={showShiny ? 'special' : 'default'} onClick={() => setShowShiny((v) => !v)}>
              {showShiny ? '✦ Shiny' : 'Normal'}
            </Button>

            {/* Starting a hunt belongs here rather than in the Nursery: you
                choose a *species* to chase, and this is the page about one. */}
            <HuntButton speciesId={species.id} name={species.displayName} />

            <div className="stack-sm">
              <div className="mono tiny muted">{dexNumber(species.id)}</div>
              <h1 style={{ fontSize: 'var(--text-xl)' }}>{species.displayName}</h1>
              <div className="row" style={{ justifyContent: 'center' }}>
                {species.types.map((type) => <TypeChip key={type} type={type} />)}
              </div>
              {(species.isLegendary || species.isMythical) && (
                <span className="chip tag-legendary">{species.isMythical ? 'Mythical' : 'Legendary'}</span>
              )}
            </div>

            {species.flavorText && <p className="tiny muted" style={{ fontStyle: 'italic' }}>"{species.flavorText}"</p>}
          </div>
        </Panel>

        <div className="stack">
          <Panel title="◈ Base stats">
            <div className="stack-sm">
              {(Object.entries(species.baseStats) as Array<[string, number]>).map(([key, value]) => (
                <div key={key} className="stat-row">
                  <span className="stat-row__name">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <div className="stat-row__track">
                    <div
                      className={`stat-row__fill ${value >= 100 ? 'stat-row__fill--high' : value <= 45 ? 'stat-row__fill--low' : ''}`}
                      style={{ width: `${Math.min(100, (value / 180) * 100)}%` }}
                    />
                  </div>
                  <span className="stat-row__value">{value}</span>
                </div>
              ))}
            </div>
            <hr className="divider" />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
              <Fact label="Height" value={`${(species.height / 10).toFixed(1)} m`} />
              <Fact label="Weight" value={`${(species.weight / 10).toFixed(1)} kg`} />
              <Fact label="Growth" value={titleCase(species.growthRate)} />
              <Fact label="Egg groups" value={species.eggGroups.map((g) => titleCase(g)).join(', ') || '—'} />
              {species.habitat && <Fact label="Habitat" value={titleCase(species.habitat)} />}
            </div>
          </Panel>

          {(evolvesFrom.length > 0 || evolvesTo.length > 0) && (
            <Panel title="⟳ Evolution family">
              <div className="stack-sm">
                {evolvesFrom.map((entry) => (
                  <Link key={entry.id} to={`/dex/${entry.id}`} className="pxwell row" style={{ padding: 8, gap: 10 }}>
                    <img className="pixel" src={entry.sprite} alt="" style={{ width: 48, height: 48 }} />
                    <div style={{ flex: 1 }}>
                      <div className="mono tiny">{entry.name}</div>
                      <div className="tiny muted">evolves into {species.displayName} — {entry.requirementText}</div>
                    </div>
                  </Link>
                ))}
                {evolvesTo.map((entry) => (
                  <Link key={entry.id} to={`/dex/${entry.id}`} className="pxwell row" style={{ padding: 8, gap: 10 }}>
                    <img className="pixel" src={entry.sprite} alt="" style={{ width: 48, height: 48 }} />
                    <div style={{ flex: 1 }}>
                      <div className="mono tiny">{entry.name}</div>
                      <div className="tiny muted">{entry.requirementText}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </Panel>
          )}

          {/* Gated by the Journal: only families you have hatched this from. */}
          {foundIn.length > 0 && (
            <Panel title="◔ Found in" hint="from your Journal">
              <div className="row row--wrap">
                {foundIn.map((family) => (
                  <Link
                    key={family.key}
                    to={`/journal/${family.key}`}
                    className="pxwell row"
                    style={{ padding: 8, gap: 8 }}
                  >
                    <EggArt appearance={family.appearance} size="xs" state="still" label={family.name} />
                    <span className="tiny mono">{family.name}</span>
                  </Link>
                ))}
              </div>
            </Panel>
          )}

          <Panel title="◈ Yours" hint={owned.length > 0 ? `${owned.length} owned` : ''}>
            {owned.length > 0 ? (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
                {owned.map((pokemon) => (
                  <PokemonCard key={pokemon.id} pokemon={pokemon} to={`/pokemon/${pokemon.id}`} />
                ))}
              </div>
            ) : (
              <EmptyState art="◌" title="Not caught yet" action={<Link className="btn btn--primary btn--sm" to="/eggs">Find an egg</Link>}>
                {foundIn.length > 0
                  ? `Try a ${foundIn[0]!.name}.`
                  : "You haven't hatched this one yet, so no egg family is recorded for it."}
              </EmptyState>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="tiny muted mono" style={{ fontSize: '0.5625rem', letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </div>
      <div className="mono tiny">{value}</div>
    </div>
  );
}
